"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  addCurrencyToStorage,
  getCurrencyFromStorage,
} from "@/app/utils/services/service/localStorageService";
import { COUNTRY_TO_CURRENCY_CODE } from "@/app/common/consts/currencies";
import { useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";

export type CurrencyOption = { label: string; value: string; type?: string };

const DEFAULT_CURRENCY = {
  label: "USD",
  value: "USD",
};

const getCurrencyByCode = (
  currencies: CurrencyOption[],
  code?: string | null
) =>
  currencies.find(
    (currency) => currency.value.toUpperCase() === code?.toUpperCase()
  );

const getFallbackCurrency = (currencies: CurrencyOption[]) =>
  getCurrencyByCode(currencies, DEFAULT_CURRENCY.value) ??
  currencies[0] ??
  DEFAULT_CURRENCY;

export const CurrencyContext = createContext({
  currency: DEFAULT_CURRENCY as CurrencyOption,
  setUpdateCurrency: (op: CurrencyOption) => {},
  currencies: [DEFAULT_CURRENCY] as unknown as CurrencyOption[],
  rate: 1,
  currencyCode: "",
});

const CurrencyProvider = ({ children }: { children: React.ReactNode }) => {
  const locale = useLocale();
  const savedCurrency = getCurrencyFromStorage();
  const router = useRouter();
  const searchParams = useSearchParams();
  const urlCurrency = searchParams.get("currency")?.toUpperCase();
  const getInitialCurrency = (): CurrencyOption => {
    if (urlCurrency) {
      return {
        label: urlCurrency,
        value: urlCurrency,
      };
    }

    if (savedCurrency?.value && savedCurrency.value !== "USD") {
      return savedCurrency;
    }

    return DEFAULT_CURRENCY;
  };
  const initialCurrency = getInitialCurrency();

  const [currency, setCurrency] = useState(initialCurrency);
  const [updatedCurrency, setUpdateCurrency] = useState(initialCurrency);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    if (currencies.length > 0) {
      const isCurrencyValid = currencies.some(
        (c) => c.value.toUpperCase() === currency.value.toUpperCase()
      );
      if (!isCurrencyValid) {
        const fallbackCurrency = getFallbackCurrency(currencies);
        setUpdateCurrency(fallbackCurrency);
        addCurrencyToStorage(JSON.stringify(fallbackCurrency));
      }
    }
  }, [currency, currencies]);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    if (updatedCurrency?.value === "USD") {
      setRate(1);
      setCurrency(updatedCurrency);
      const params = new URLSearchParams(searchParams.toString());
      params.set("currency", updatedCurrency.value);
      router.replace(`?${params.toString()}`, { scroll: false });
      addCurrencyToStorage(JSON.stringify({ ...updatedCurrency }));
    } else {
      updateRate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedCurrency]);

  async function updateRate() {
    try {
      const { data } = await axios.get(
        `/api/currency/convert?currencyCode=${updatedCurrency.value}&lang=${locale}`
      );
      if (data.rate) {
        setRate(Number(data?.rate));
        setCurrency(updatedCurrency);
        addCurrencyToStorage(JSON.stringify(updatedCurrency));
        const params = new URLSearchParams(searchParams.toString());
        params.set("currency", updatedCurrency.value);
        router.replace(`?${params.toString()}`, { scroll: false });
      } else {
        setUpdateCurrency(getFallbackCurrency(currencies));
      }
    } catch (err) {
      setUpdateCurrency(getFallbackCurrency(currencies));
    }
  }

  async function fetchCurrencies() {
    try {
      const res = await fetch(`/api/currency?lang=${locale}`);
      const data = await res.json();
      const nextCurrencies = Array.isArray(data?.currencies)
        ? data.currencies
        : [];

      if (
        savedCurrency &&
        savedCurrency.value === "USD" &&
        savedCurrency?.isDefault
      ) {
        const didUseLocationCurrency =
          await getUserLocationInfo(nextCurrencies);
        if (didUseLocationCurrency) {
          setCurrencies(nextCurrencies);
          return;
        }
      }

      const requestedCurrency = getCurrencyByCode(
        nextCurrencies,
        urlCurrency ?? savedCurrency?.value
      );

      if (requestedCurrency) {
        setUpdateCurrency(requestedCurrency);
      } else {
        setUpdateCurrency(getFallbackCurrency(nextCurrencies));
      }

      setCurrencies(nextCurrencies);
    } catch (error) {
      console.log("Error fetching currencies", error);
    }
  }

  const getUserLocationInfo = async (newCurrencies: CurrencyOption[]) => {
    try {
      const res = await fetch(`/api/currency/user-currency?lang=${locale}`);
      const data = await res.json();
      const currencyCode = COUNTRY_TO_CURRENCY_CODE[data?.countryCode];
      if (!currencyCode) return false;
      const isCurrencyValid = newCurrencies.some(
        (c) => c.value.toUpperCase() === currencyCode.toUpperCase()
      );

      if (!isCurrencyValid) return false;

      setUpdateCurrency({
        label: currencyCode,
        value: currencyCode,
      });
      return true;
    } catch (error) {
      console.error("Failed to fetch IP/location:", error);
      return false;
    }
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        currencies,
        rate,
        currencyCode: currency?.value,
        setUpdateCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrencyContext = () => useContext(CurrencyContext);

export default CurrencyProvider;
